import './style.scss'

import SubmitRatingModal from './components/SubmitRatingModal'
import RatingsComponent from './components/Ratings'
import DetailedRatings from './components/DetailedRatings'

import { isMobile } from './utils/responsive'
import {
  InternalPluginProps,
  InternalComponentAPI,
  Maybe,
  PluginAPI,
  PluginProps,
  UpdatedRatings
} from './utils/types'
import { SubmitRatingModalAPI } from './components/SubmitRatingModal'
import { getTranslationsWithFallback } from './utils/translations'
import { createLogger } from './utils/logger'
import { generateId } from './utils/uuid'

/**
 * Select the dom elements to mount the plugin
 * @param ratingTarget Target for the rating anchor component
 * @param detailedRatingTarget Target for the detailed ratings component (mobile only)
 */
const selectTargets = ({
  ratingTarget,
  detailedRatingTarget
}: {
  ratingTarget: string
  detailedRatingTarget?: string
}): [Element, Element | null] => {
  const ratingsTargetEl = document.querySelector(ratingTarget)
  if (!ratingsTargetEl) {
    throw new Error('Invalid ratings target element')
  }

  let detailedRatingEl: Maybe<Element> = null
  if (detailedRatingTarget) {
    detailedRatingEl = document.querySelector(detailedRatingTarget)
  }
  return [ratingsTargetEl, detailedRatingEl]
}

/**
 * Decorate the external plugin props with internal API and fallback translations.
 * @param props external plugin props
 * @param internalAPI internal API
 */
const createInternalPluginProps = (
  props: PluginProps,
  internalAPI: InternalComponentAPI
): InternalPluginProps => {
  return {
    ...props,
    internalAPI,
    pluginId: generateId(props.itemId),
    logger: createLogger(props.logLevel),
    translations: getTranslationsWithFallback(props.translations)
  }
}

/**
 * Ratings plugin entry point
 * @param externalProps external plugin props
 * @returns plugin external API
 */
export const Ratings = (externalProps: PluginProps): PluginAPI => {
  let ratings: Maybe<InternalComponentAPI> = null
  let detailedRatings: Maybe<InternalComponentAPI> = null
  let submitRatings: Maybe<SubmitRatingModalAPI> = null

  // Setup internal API
  const internalAPI: InternalComponentAPI = {
    updateRatings: (updatedRatings: UpdatedRatings) => {
      ratings?.updateRatings(updatedRatings)
      detailedRatings?.updateRatings(updatedRatings)
      submitRatings?.updateRatings(updatedRatings)
    },
    destroy: () => {
      ratings?.destroy()
      detailedRatings?.destroy()
      submitRatings?.destroy()
    }
  }
  const props = createInternalPluginProps(externalProps, internalAPI)
  const [ratingsTargetEl, detailedRatingEl] = selectTargets(props)

  props.logger.debug('Plugin initialized:', externalProps)
  // Init child components
  submitRatings = SubmitRatingModal({
    ...props,
    modalParent: document.body
  })

  ratings = RatingsComponent({
    ...props,
    target: ratingsTargetEl,
    onSubmitRatingClick: submitRatings.show,
    onMobileRatingClick: () => detailedRatingEl?.scrollIntoView()
  })

  if (isMobile() && detailedRatingEl) {
    detailedRatings = DetailedRatings({
      ...props,
      target: detailedRatingEl,
      onSubmitRatingClick: submitRatings.show
    })
    // TODO: Scroll to DetailedRatings on RatingsComponent click
  }

  return {
    showSubmitRatingModal: submitRatings.show
  }
}
